import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Target, ShieldCheck, Users } from "lucide-react";

const HERO_IMG = "/images/hero.jpg";

const LINES = [
  <>Corsi d'informatica</>,
  <>non a ore, ma con</>,
  <><span className="text-[#5FC6D6]">certezza d'imparare.</span></>,
];

const STATS = [
  { icon: Target, value: "166", label: "Lezioni teorico-pratiche" },
  { icon: ShieldCheck, value: "100%", label: "Garanzia di Profitto" },
  { icon: Users, value: "N. chiuso", label: "Classi seguite da vicino" },
];

export default function KineticHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden bg-[#0C2930]" data-testid="hero">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt="Tre allievi davanti a un computer, seguiti dall'insegnante al loro fianco"
          className="h-full w-full object-cover"
          style={{ objectPosition: "center 42%" }}
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0C2930]/75 via-[#0C2930]/45 to-[#0C2930]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C2930]/80 via-transparent to-transparent" />
      </motion.div>
      <div className="animate-float-slow pointer-events-none absolute left-[-8%] top-[16%] h-[380px] w-[380px] rounded-full bg-[#2997AB]/25 blur-3xl" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-40 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="font-script text-2xl text-[#7AD9E8] sm:text-3xl"
        >
          Non corsi a ore ma a obiettivo, con la certezza del risultato
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-3 font-plex text-[11px] uppercase tracking-[0.32em] text-[#9DBFC7] sm:text-xs"
        >
          Scuola Didattica Sicura — Brescia, in tutta Italia
        </motion.p>

        <h1 className="text-balance mt-6 font-display text-[13vw] font-semibold leading-[0.98] tracking-tight text-white sm:text-7xl lg:text-[92px]">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-[#CFE3E8] sm:text-lg"
        >
          Insegnanti preparati e disponibili, un metodo metodico e ordinato, e la certezza del
          risultato: non corsi a ore, ma a obiettivo.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/corsi"
            data-testid="hero-cta-corsi"
            className="group inline-flex items-center gap-2 rounded-full bg-[#2997AB] px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#2FA7BC]"
          >
            Scopri i corsi
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/contatti"
            data-testid="hero-cta-contatti"
            className="inline-flex items-center gap-2 rounded-full border border-white/50 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors duration-300 hover:border-[#7AD9E8] hover:text-[#7AD9E8]"
          >
            Richiedi informazioni
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-14 grid max-w-3xl grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/15 bg-white/15 backdrop-blur-md sm:grid-cols-3"
        >
          {STATS.map((s) => (
            <div key={s.label} className="flex items-center gap-4 bg-[#0C2930]/70 px-6 py-5">
              <s.icon className="h-6 w-6 shrink-0 text-[#5FC6D6]" />
              <div>
                <p className="font-display text-xl font-semibold text-white">{s.value}</p>
                <p className="text-xs text-[#9DBFC7]">{s.label}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 right-6 hidden text-[#9DBFC7] md:block"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.div>
    </section>
  );
}
